import React, { useCallback, useEffect, useMemo, useState } from "react";
import { MaterialReactTable, MRT_ColumnDef } from "material-react-table";
import { MRT_Localization_ES } from "material-react-table/locales/es";
import { useSnackbar } from "@components";
import {
  Box,
  Grid,
  IconButton,
  Tooltip,
  Alert,
  Typography,
} from "@mui/material";
import {
  Refresh as RefreshIcon,
  FileDownload as FileDownloadIcon,
  Cancel as CancelIcon,
  WifiOff as WifiOffIcon,
  Sync as SyncIcon,
} from "@mui/icons-material";

import {
  downloadTruckFilesById,
  generateWfTruckPdfReportbyId,
  useWorkflowTrucks,
  workflowTruckDelete,
} from "../../../../libs/apis";
import { WorkflowTruckI } from "../../../../libs/interfaces";
import { HEIGHT_TABLE_CONTAINER } from "../../../../libs/constants";
import {
  ExcelComponent,
  NetworkStatusAlert,
  PdfElement,
  SendReport,
} from "../../../../libs/shared";
import { useWidth } from "../../../../libs/hooks";
import { useNavigate } from "react-router-dom";
import "../../../../libs/css/list.css";
import {
  Button as ButtonAntd,
  Tooltip as TooltipAntd,
  Popconfirm,
  message,
  DatePicker,
  ConfigProvider,
  Modal,
  Empty,
} from "antd";
import {
  DeleteFilled,
  EditOutlined,
  FilePdfOutlined,
  FileZipFilled,
  FilterOutlined,
  CloudSyncOutlined,
} from "@ant-design/icons";
import { ShowContainer } from "./show-container";
import ComponentModalTarjaText from "./tarja-modal";

const titleExport = "Workflow";
const routeEdit = "/truck/edit/";
const { RangePicker } = DatePicker;

interface CachedDataI {
  data?: {
    dato: WorkflowTruckI[];
    total?: number;
  };
}

interface PaginationStateI {
  pageIndex: number;
  pageSize: number;
}

// Componente de filtros para reutilizar
const FiltersComponent: React.FC<{
  width: string;
  disabled?: boolean;
  rangeVal: any;
  setRangeVal: any;
  setRange: any;
}> = React.memo(
  ({ width, disabled = false, rangeVal, setRangeVal, setRange }) => {
    const isMobile = width === "xs";

    return (
      <Grid item xs={isMobile ? 12 : "auto"}>
        <ConfigProvider
          theme={{
            components: {
              DatePicker: {
                zIndexPopup: 10500,
              },
            },
          }}
        >
          <RangePicker
            value={rangeVal}
            onChange={(dates, date) => {
              setRangeVal(dates);
              setRange(date);
            }}
            disabled={disabled}
            style={{
              width: isMobile ? "100%" : "auto",
              marginBottom: isMobile ? "8px" : "0",
            }}
            getPopupContainer={() => document.body}
            dropdownClassName={isMobile ? "mobile-date-picker-fixed" : ""}
            placeholder={["Fecha inicio", "Fecha fin"]}
            allowClear
            placement={isMobile ? "topLeft" : "bottomLeft"}
          />
        </ConfigProvider>
      </Grid>
    );
  }
);

// Modal para filtros en móvil
const MobileFiltersModal: React.FC<{
  visible: boolean;
  onClose: () => void;
  width: string;
  disabled: boolean;
  rangeVal: any;
  setRangeVal: any;
  setRange: any;
}> = ({
  visible,
  onClose,
  width,
  disabled,
  rangeVal,
  setRangeVal,
  setRange,
}) => {
  return (
    <ConfigProvider
      theme={{
        components: {
          Modal: {
            // zIndexPopup: 9999,
          },
          DatePicker: {
            zIndexPopup: 10500,
          },
        },
      }}
    >
      <Modal
        title="Filtros"
        open={visible}
        onCancel={onClose}
        footer={null}
        centered
        width="90%"
        style={{
          maxWidth: "400px",
          zIndex: 9999,
        }}
        bodyStyle={{
          padding: "16px",
          maxHeight: "70vh",
          overflowY: "auto",
        }}
        maskStyle={{
          zIndex: 9998,
        }}
        wrapClassName="mobile-filters-modal"
        destroyOnClose
        maskClosable={true}
      >
        <Grid container spacing={2} direction="column">
          <FiltersComponent
            width={width}
            disabled={disabled}
            rangeVal={rangeVal}
            setRangeVal={setRangeVal}
            setRange={setRange}
          />
        </Grid>
      </Modal>
    </ConfigProvider>
  );
};

const WorkflowListComponent = () => {
  const width = useWidth();
  const isMobile = width === "xs" || width === "sm" || width === "md";
  const navigate = useNavigate();
  const [messageApi, contextHolder] = message.useMessage();
  const { enqueueSnackbar } = useSnackbar();
  const [sorting, setSorting] = useState([{ id: "id", desc: true }]);
  const [pagination, setPagination] = useState<PaginationStateI>({
    pageIndex: 0,
    pageSize: width === "ab" ? 25 : width === "xl" ? 20 : 15,
  });
  const [range, setRange] = React.useState([] as unknown as [string, string]);
  const [rangeVal, setRangeVal] = React.useState<any>([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [showMobileFilters, setShowMobileFilters] =
    React.useState<boolean>(false);

  // Estados offline
  const [isOffline, setIsOffline] = React.useState<boolean>(!navigator.onLine);

  const { workflowTrucksQuery } = useWorkflowTrucks({
    sorting,
    pagination,
    globalFilter,
    range,
    rangeVal,
  });
  const { data, isLoading, refetch, isFetching, isRefetching } =
    workflowTrucksQuery;
  const [rowSelection, setRowSelection] = useState({});

  const [isDeleting, setDeleting] = useState(false);
  const [getExportData, setExportData] = useState([]);
  const [getExportHeader, setExportHeader] = useState([]);
  const [isContinuoToExport, setContinuoToExport] = useState(false);
  const [isReadyExport, setReadyExport] = useState(false);
  const [isGeneratingPdf, setGeneratingPdf] = useState(false);
  const [downloadFile, setDownloadFile] = React.useState<boolean>(false);

  React.useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  useEffect(() => {
    if (isContinuoToExport) {
      setContinuoToExport(false);
      setReadyExport(true);
    }
  }, [isContinuoToExport]);

  const columns = useMemo<MRT_ColumnDef<WorkflowTruckI>[]>(
    () => [
      {
        accessorKey: "container",
        id: "container",
        header: "Contenedor",
        minSize: 100,
        size: 300,
        enableClickToCopy: true,
        Cell: ({ renderedCellValue, row }) => (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "1rem",
              "&:hover": {
                cursor: "pointer",
              },
            }}
          >
            <a className="b-proform">{renderedCellValue}</a>
          </Box>
        ),
      },
      {
        accessorKey: "client",
        id: "client",
        header: "Cliente",
        minSize: 150,
        size: 150,
        enableClickToCopy: true,
      },
      {
        accessorKey: "producer",
        id: "producer",
        header: "Productor",
        minSize: 150,
        size: 150,
        enableClickToCopy: true,
      },
      {
        accessorKey: "createdAt",
        id: "createdAt",
        header: "Creacion",
        minSize: 150,
        size: 150,
      },
      {
        accessorKey: "userCreation",
        id: "userCreation",
        header: "Usuario Creación",
        minSize: 100,
        size: 200,
      },
    ],
    []
  );

  const handleDeleteRow = useCallback(
    async (row: WorkflowTruckI) => {
      if (isOffline) {
        message.warning("No se pueden eliminar registros sin conexión", 3);
        return;
      }

      setDeleting(true);
      try {
        const { data: dataWorkflow } = await workflowTruckDelete(row.id!);
        enqueueSnackbar(dataWorkflow, { variant: "success" });
        refetch();
        setDeleting(false);
      } catch (err) {
        enqueueSnackbar((err as Error).message, { variant: "error" });
        setDeleting(false);
      }
    },
    [isOffline, enqueueSnackbar, refetch]
  );

  const handleExportRows = (rows: any, columnsShow: any) => {
    const headersActive = columnsShow
      .slice(2)
      .filter((el: any) => el.getIsVisible())
      .map((el: any) => ({
        label: el.columnDef.header,
        id: el.columnDef.id,
        width: el.columnDef.size,
        textAlign: "left",
        show: true,
        fontSize: "10px",
      }));

    rows = rows.map((el: any) => el.original);

    setExportData(rows);
    setExportHeader(headersActive);
    setContinuoToExport(true);
  };

  const handleClickPdf = () => {
    setTimeout(() => {
      setReadyExport(false);
      setExportData([]);
      setExportHeader([]);
    }, 1500);
  };

  const handleResetExport = () => {
    setReadyExport(false);
    setExportData([]);
    setExportHeader([]);
  };

  const handleNavigate = useCallback(
    (row: WorkflowTruckI) => {
      if (isOffline) {
        message.warning("En modo offline solo puede ver los detalles", 3);
      }

      const routePath = routeEdit + row.id;
      navigate(routePath);
    },
    [isOffline, navigate]
  );

  const handlePdf = async (row: WorkflowTruckI) => {
    if (isOffline) {
      message.warning("No se pueden generar PDFs sin conexión", 3);
      return;
    }

    try {
      setGeneratingPdf(true);

      messageApi.open({
        key: "generatePdfKey1",
        type: "loading",
        content: "Generando Pdf",
        duration: 10,
      });

      const { data: dataPdf } = await generateWfTruckPdfReportbyId(row.id!);
      messageApi.destroy("generatePdfKey1");
      messageApi.open({
        key: "generatePdfKey4",
        type: "loading",
        content: "Procesando Pdf",
        duration: 2,
        onClose: () => {
          message.success("Descargando PDF", 2);
        },
      });

      const downloadLink = document.createElement("a");
      downloadLink.target = "__blank";
      downloadLink.href = `data:application/pdf;base64,${dataPdf}`;
      downloadLink.download = `Reporte-Camión-${row.container}.pdf`;
      downloadLink.click();
      setGeneratingPdf(false);
    } catch (err: any) {
      messageApi.destroy();
      setGeneratingPdf(false);
      message.error(`Error al generar el pdf: ${err.toString()}`);
    }
  };

  const handleDownloadFiles = React.useCallback(
    async (row: WorkflowTruckI): Promise<void> => {
      if (isOffline) {
        message.warning("No se pueden descargar archivos sin conexión", 3);
        return;
      }

      try {
        setDownloadFile(true);
        const blobData = await downloadTruckFilesById(row.id!);

        const url = window.URL.createObjectURL(blobData! as any);
        setDownloadFile(false);

        const a = document.createElement("a");
        a.href = url;
        a.download = `reporte_${row.container || ""}_${row.id}.zip`;
        document.body.appendChild(a);

        a.click();

        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

        message.success("Archivo descargado correctamente", 1);
      } catch (err) {
        setDownloadFile(false);
        enqueueSnackbar((err as Error).message, { variant: "error" });
      }
    },
    [isOffline, enqueueSnackbar]
  );

  const OfflineState: React.FC = () => (
    <Empty
      image={<WifiOffIcon style={{ fontSize: 60, color: "#faad14" }} />}
      description={
        <span>Sin conexión a internet. No se pudieron cargar los datos.</span>
      }
    >
      <ButtonAntd
        type="primary"
        icon={<SyncIcon />}
        onClick={() => refetch()}
        style={{ marginRight: 8 }}
      >
        Intentar nuevamente
      </ButtonAntd>
    </Empty>
  );

  if (isOffline) return <OfflineState />;

  return (
    <>
      {contextHolder}

      <NetworkStatusAlert onRefresh={refetch} />

      <MobileFiltersModal
        visible={showMobileFilters}
        onClose={() => setShowMobileFilters(false)}
        width={width}
        disabled={isOffline}
        rangeVal={rangeVal}
        setRangeVal={setRangeVal}
        setRange={setRange}
      />

      <MaterialReactTable
        displayColumnDefOptions={{
          "mrt-row-actions": {
            muiTableHeadCellProps: {
              align: "center",
            },
            size: 220,
            maxSize: 220,
          },
        }}
        enableStickyHeader
        enableStickyFooter
        enableDensityToggle
        enableColumnOrdering
        enableColumnResizing
        enableEditing
        enableGlobalFilter
        enableRowSelection={!isOffline}
        enableColumnFilters={false}
        manualFiltering
        manualPagination
        manualSorting
        muiTableContainerProps={({ table }) => {
          return {
            sx: {
              minHeight: table.options.state.isFullScreen
                ? `calc(100vh - 100px)`
                : `calc(100vh - ${HEIGHT_TABLE_CONTAINER})`,
              maxHeight: table.options.state.isFullScreen
                ? `calc(100vh - 100px)`
                : `calc(100vh - ${HEIGHT_TABLE_CONTAINER})`,
              height: table.options.state.isFullScreen
                ? `calc(100vh - 100px)`
                : `calc(100vh - ${HEIGHT_TABLE_CONTAINER})`,
              zIndex: 1,
            },
          };
        }}
        columns={columns}
        data={data?.data?.dato || []}
        initialState={{
          density: "compact",
          rowSelection,
          sorting,
        }}
        state={{
          isLoading: (isLoading || isFetching || isRefetching) && !isOffline,
          globalFilter,
          rowSelection,
          pagination,
          sorting,
          showColumnFilters: false,
        }}
        renderEmptyRowsFallback={() => {
          return isOffline ? (
            <OfflineState />
          ) : (
            <span>No se encontraron registros</span>
          );
        }}
        rowCount={data?.data?.total ?? 0}
        onRowSelectionChange={setRowSelection}
        onPaginationChange={setPagination}
        onSortingChange={setSorting}
        onGlobalFilterChange={setGlobalFilter}
        renderTopToolbarCustomActions={({ table }) => (
          <Grid
            alignItems="center"
            container
            justifyContent="flex-start"
            spacing={1}
            sx={{ width: "auto" }}
          >
            <Grid item>
              <Tooltip title="Refrescar">
                <span>
                  <IconButton
                    onClick={() => refetch()}
                    color="warning"
                    disabled={isOffline && !navigator.onLine}
                  >
                    <RefreshIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Grid>

            {isMobile ? (
              <Grid item>
                <ButtonAntd
                  icon={<FilterOutlined />}
                  disabled={isOffline}
                  onClick={() => setShowMobileFilters(true)}
                >
                  Filtros
                </ButtonAntd>
              </Grid>
            ) : (
              <FiltersComponent
                width={width}
                disabled={isOffline}
                rangeVal={rangeVal}
                setRangeVal={setRangeVal}
                setRange={setRange}
              />
            )}

            <Grid item sx={{ display: "grid", placeContent: "center" }}>
              <Tooltip arrow title="Export Data">
                <span>
                  <IconButton
                    disabled={
                      (!table.getIsSomeRowsSelected() &&
                        !table.getIsAllRowsSelected()) ||
                      isReadyExport ||
                      isOffline
                    }
                    onClick={() =>
                      handleExportRows(
                        table.getSelectedRowModel().rows,
                        table.getAllColumns()
                      )
                    }
                    color="success"
                  >
                    <FileDownloadIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Grid>
            <Grid item sx={{ display: "grid", placeContent: "center" }}>
              {isReadyExport ? (
                <PdfElement
                  title="Reporte de Camiones"
                  data={getExportData}
                  headersColumn={getExportHeader}
                  HandleClickPdf={handleClickPdf}
                  isSmall={true}
                />
              ) : null}
            </Grid>
            <Grid item sx={{ display: "grid", placeContent: "center" }}>
              {isReadyExport ? (
                <ExcelComponent
                  data={getExportData}
                  titulo={titleExport}
                  headersColumn={getExportHeader}
                  isEcuafact={false}
                />
              ) : null}
            </Grid>
            <Grid item sx={{ display: "grid", placeContent: "center" }}>
              {isReadyExport ? (
                <Tooltip arrow title="Cancelar Exportación" placement="top">
                  <IconButton
                    onClick={handleResetExport}
                    style={{ backgroundColor: "#ECF0F1 ", color: "red" }}
                  >
                    <CancelIcon />
                  </IconButton>
                </Tooltip>
              ) : null}
            </Grid>
          </Grid>
        )}
        renderRowActions={({ row, table }) => (
          <Box sx={{ display: "flex", gap: "0.4rem" }}>
            <TooltipAntd arrow={false} title="Editar">
              <ButtonAntd
                type="dashed"
                size="small"
                shape="circle"
                onClick={() => handleNavigate(row.original)}
                icon={<EditOutlined />}
                disabled={isOffline}
              />
            </TooltipAntd>
            <TooltipAntd arrow={false} title="Multimedia">
              <ButtonAntd
                type="dashed"
                size="small"
                shape="circle"
                onClick={() => handleDownloadFiles(row.original)}
                icon={<FileZipFilled />}
                disabled={isOffline || downloadFile}
                loading={downloadFile}
              />
            </TooltipAntd>
            <TooltipAntd arrow={false} title="Eliminar">
              <Popconfirm
                title="Eliminar Workflow"
                description="Seguro desea eliminar este Workflow?"
                onConfirm={() => handleDeleteRow(row.original)}
                okText="Si"
                cancelText="No"
                disabled={isOffline}
              >
                <ButtonAntd
                  type="dashed"
                  danger
                  loading={isDeleting}
                  disabled={isDeleting || isOffline}
                  size="small"
                  shape="circle"
                  icon={<DeleteFilled />}
                />
              </Popconfirm>
            </TooltipAntd>
            <ButtonAntd
              loading={isGeneratingPdf}
              disabled={isGeneratingPdf || isOffline}
              size="small"
              type="dashed"
              onClick={() => handlePdf(row.original)}
              icon={<FilePdfOutlined />}
            />
            <SendReport
              id={row.original.id!}
              typeWf="truck"
              disabled={isOffline}
            />
            <ShowContainer id={row.original.id!} disabled={isOffline} />
            <ComponentModalTarjaText
              containerId={row.original.id!}
              containerCode={row.original.container!}
              isOffline={isOffline}
            />
          </Box>
        )}
        localization={MRT_Localization_ES}
        selectAllMode="all"
        positionToolbarAlertBanner="none"
      />
    </>
  );
};

export default WorkflowListComponent;